import { JSX, useImperativeHandle, useRef } from 'react';
import './style.scss';
import IMultiFacet from '../../../models/multi-facet.model';
import { MultiFacetType } from '../../../models/multi-facet-type.enum';
import SelectedFilterValue from '../SelectedFilterValue';
import { type IAutocomplete } from '../AutocompleteBase';
import ValidationInfo from '../../../validation/validation-info';
import type { IAutocompleteItem } from '../../../models/autocomplete-item.model';
import LineListShowcase from '../LineListShowcase';
import MunicipalityListShowcase from '../MunicipalityListShowcase';
import MultiFacetAutocomplete from '../MultiFacetAutocomplete';
import { MultiFacetSelectorLayout } from './multi-facet-selector-layout.enum';
import { MultiFacetHelper } from '../../../helpers/multi-facet.helper';
import { ClientSideStorageItems } from '../../../services/client-side-storage.service';

// === INTERFACES AND ENUMS ===
export interface IMultiFacetSelector {
  clear: () => void;
}

interface IMultiFacetSelectorProps {
  id: string;
  labelText: string;
  placeholderText: string;
  noResultsText: string;
  selectedMultiFacetItems: IMultiFacet[];
  layout?: MultiFacetSelectorLayout;
  commonSearches?: IAutocompleteItem[];
  onChange: (selectedMunicipalities: IMultiFacet[]) => void;
}

function MultiFacetSelector({
  id,
  labelText,
  placeholderText,
  noResultsText,
  selectedMultiFacetItems,
  layout = MultiFacetSelectorLayout.vertical,
  commonSearches,
  onChange,
  ref,
}: IMultiFacetSelectorProps & { ref?: React.Ref<IMultiFacetSelector> }) {
  const multiFacetAutocompleteRef = useRef<IAutocomplete>(null);

  useImperativeHandle(ref, () => ({
    clear() {
      multiFacetAutocompleteRef.current?.clear();
    },
  }));

  function handleMultiFacetAutocompleteSelect(selectedMultiFacetItem: IMultiFacet | undefined): void {
    if (!selectedMultiFacetItem) {
      return;
    }

    if (selectedMultiFacetItems.some((m) => MultiFacetHelper.getAutocompleteItemId(m) === MultiFacetHelper.getAutocompleteItemId(selectedMultiFacetItem))) {
      multiFacetAutocompleteRef.current?.clear();
    } else {
      multiFacetAutocompleteRef.current?.addSelectedItemToHistory(selectedMultiFacetItem);
      multiFacetAutocompleteRef.current?.clear();
      onChange([...selectedMultiFacetItems, selectedMultiFacetItem]);
    }
  }

  function handleSelectedAutocompleteItemRemove(gid: string) {
    onChange(selectedMultiFacetItems.filter((item) => MultiFacetHelper.getAutocompleteItemId(item) !== gid));
  }

  function getChildShowcase(selectedItem: IMultiFacet): JSX.Element | null {
    if (selectedItem.type === MultiFacetType.Line && selectedItem.line) {
      return (
        <LineListShowcase
          lines={[selectedItem.line]}
          showTrainDirectionName={true}
          showNonTrainDirectionName={true}
          directionAsStrong={false}
          showAppliesFromDateOnFutureLines={true}
        ></LineListShowcase>
      );
    } else if (selectedItem.type === MultiFacetType.Municipality && selectedItem.municipality) {
      return <MunicipalityListShowcase municipalities={[selectedItem.municipality!]} />;
    }
    return null;
  }

  return (
    <div className={`multi-facet-selector mb-n2 ${layout === MultiFacetSelectorLayout.horizontal ? 'multi-facet-selector--layout-horizontal' : ''}`}>
      <div className="multi-facet-selector__autocomplete-wrapper mb-2">
        <MultiFacetAutocomplete
          id={id}
          ref={multiFacetAutocompleteRef}
          labelText={labelText}
          labelClassName="form-label"
          noResultsText={noResultsText}
          placeholderText={placeholderText}
          historyProps={{ localStorageItem: ClientSideStorageItems.LocalStorage.MultiFacetAutocompleteHistory, commonSearches }}
          state={{
            value: undefined,
            validationInfo: new ValidationInfo('', []),
          }}
          onSelect={handleMultiFacetAutocompleteSelect}
        />
      </div>

      {selectedMultiFacetItems.length > 0 && (
        <div className="multi-facet-selector__selected-items d-flex flex-row flex-wrap">
          {selectedMultiFacetItems.map((selectedItem) => {
            const itemId = MultiFacetHelper.getAutocompleteItemId(selectedItem);
            if (!itemId) return null;
            return (
              <SelectedFilterValue
                id={itemId}
                key={itemId}
                ariaLabel={MultiFacetHelper.getAutocompleteItemDisplayName(selectedItem) ?? ''}
                className="mb-2 me-2"
                children={getChildShowcase(selectedItem)}
                onRemove={() => handleSelectedAutocompleteItemRemove(itemId)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

export default MultiFacetSelector;
